(() => {
  if (typeof SideDrawer === 'undefined' || customElements.get('quote-drawer')) return;

  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  class QuoteDrawer extends SideDrawer {
    constructor() {
      super();
      this.itemsElement = this.querySelector('[data-quote-drawer-items]');
      this.statusElement = this.querySelector('[data-quote-drawer-status]');
      this.emptyElement = this.querySelector('[data-quote-drawer-empty]');
      this.footerElement = this.querySelector('[data-quote-drawer-footer]');
      this.fallbackElement = this.querySelector('[data-quote-drawer-fallback]');
      this.products = {};
      this.imageProducts = new Map();
      this.revision = 0;
      this.addEventListener('change', (event) => this.changeQuantity(event));
      this.addEventListener('click', (event) => this.changeItem(event));
      document.addEventListener('click', (event) => this.openFromLink(event));
      document.addEventListener('keydown', (event) => {
        if (event.key === ' ' && event.target.matches('[data-quote-header-link]')) {
          event.preventDefault();
          this.open(event.target);
        }
      });
      document.addEventListener('click', (event) => this.afterAdd(event), true);
      document.addEventListener('on:cart-drawer:before-open', () => {
        if (this.hasAttribute('open')) this.close();
      });
      window.addEventListener('storage', () => {
        this.syncApp();
        if (this.hasAttribute('open')) this.refresh();
      });
      this.enhanceLinks();
      document.addEventListener('shopify:section:load', () => this.enhanceLinks());
    }

    enhanceLinks() {
      document.querySelectorAll('[data-quote-header-link]').forEach((link) => {
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-controls', this.id);
        link.setAttribute('aria-expanded', String(this.hasAttribute('open')));
      });
    }

    get api() {
      const api = window.sc_raq_app_global;
      return api && ['get_added_item_list', 'set_added_item_list', 'get_quote_list_products', 'remove_item_from_list', 'raq_widget_qty'].every((key) => typeof api[key] === 'function') ? api : null;
    }

    openFromLink(event) {
      const link = event.target.closest('[data-quote-header-link]');
      if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(link);
    }

    open(opener) {
      document.querySelectorAll('cart-drawer[open], quick-add-drawer[open]').forEach((drawer) => drawer.close());
      if (!this.hasAttribute('open')) super.open(opener, this.querySelector('.js-close-drawer'));
      this.enhanceLinks();
      this.refresh();
    }

    close(callback) {
      this.revision += 1;
      super.close(callback);
      this.enhanceLinks();
      document.dispatchEvent(new CustomEvent('quote:drawer-closed'));
    }

    async ready() {
      for (let attempt = 0; attempt < 40; attempt += 1) {
        if (this.api && Array.isArray(this.api.get_added_item_list())) return this.api;
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      throw new Error('Quote app unavailable');
    }

    async refresh() {
      const revision = ++this.revision;
      this.statusElement.hidden = false;
      this.statusElement.textContent = this.dataset.loading;
      this.querySelector('[data-quote-drawer-announcement]').textContent = '';
      this.emptyElement.hidden = true;
      this.footerElement.hidden = true;
      this.fallbackElement.hidden = true;
      this.itemsElement.replaceChildren();
      this.itemsElement.setAttribute('aria-busy', 'true');
      try {
        const api = await this.ready();
        if (revision !== this.revision) return;
        const items = api.get_added_item_list();
        if (items.length && items.some((item) => !this.products[item.product_id])) {
          const data = await new Promise((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('Quote products unavailable')), 12000);
            api.get_quote_list_products((result) => {
              clearTimeout(timer);
              resolve(result);
            });
          });
          Object.assign(this.products, data.products_by_id);
        }
        if (revision !== this.revision) return;
        this.render(api.get_added_item_list());
      } catch (error) {
        if (revision !== this.revision) return;
        this.statusElement.textContent = this.dataset.error;
        this.fallbackElement.hidden = false;
      } finally {
        if (revision === this.revision) {
          this.itemsElement.removeAttribute('aria-busy');
          this.refreshFocus();
        }
      }
    }

    refreshFocus() {
      if (!this.hasAttribute('open')) return;
      const focus = this.contains(document.activeElement) ? document.activeElement : this.querySelector('.js-close-drawer');
      trapFocus(this, focus);
    }

    nativeProduct(handle) {
        if (!this.imageProducts.has(handle)) {
          const root = this.dataset.root.replace(/\/$/, '');
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 8000);
          const request = fetch(root + '/products/' + encodeURIComponent(handle) + '.js', { signal: controller.signal })
            .then((response) => response.ok ? response.json() : null)
            .catch(() => null)
            .finally(() => clearTimeout(timer));
          this.imageProducts.set(handle, request);
        }

      return this.imageProducts.get(handle);
    }

    async addVendor(holder, product) {
      const vendor = product.vendor || (await this.nativeProduct(product.handle))?.vendor;
      const template = this.querySelector('[data-quote-vendors]');
      const match = [...template.content.children].find((entry) => entry.dataset.vendor === vendor);
      if (match) holder.prepend(match.firstElementChild.cloneNode(true));
    }

    async addImage(holder, product, item) {
      const variant = product.variants?.find((value) => String(value.id) === String(item.variant_id));
      let data = variant?.featured_image || product.featured_image || product.images?.[0];
      if (!data && product.handle) {
        const imageProduct = await this.nativeProduct(product.handle);
        data = imageProduct?.variants?.find((value) => String(value.id) === String(item.variant_id))?.featured_image || imageProduct?.featured_image;
      }
      const source = typeof data === 'string' ? data : data?.src;
      if (!source) return;
      const imageLink = node('a');
      imageLink.href = this.productUrl(product, item);
      imageLink.tabIndex = -1;
      imageLink.setAttribute('aria-hidden', 'true');
      const image = node('img', 'quote-drawer__image');
      image.src = source;
      image.alt = '';
      image.width = 88;
      image.height = 112;
      image.loading = 'lazy';
      imageLink.append(image);
      holder.replaceChildren(imageLink);
    }

    async addComparePrice(group, product, variant, item) {
      let compare = Number(variant.compare_at_price || 0);
      let price = Number(variant.price);
      let convert = true;
      if (!compare && product.handle) {
        const nativeProduct = await this.nativeProduct(product.handle);
        const nativeVariant = nativeProduct?.variants?.find((entry) => String(entry.id) === String(item.variant_id));
        compare = Number(nativeVariant?.compare_at_price || 0);
        price = Number(nativeVariant?.price || 0);
        convert = false;
      }
      if (compare <= price) return;
      const rate = convert ? 1 : Number(this.api.get_current_currency_rate?.() || 1);
      const total = (compare + Number(item.total_extra_price || 0) * 100 * rate) * Number(item.qty || 1);
      const text = this.money(total, convert);
      const previous = node('s', 'price__was quote-drawer__was', text);
      previous.setAttribute('aria-label', this.dataset.regularPrice + ': ' + text);
      group.append(previous);
    }

    productUrl(product, item) {
      const root = this.dataset.root.replace(/\/$/, '');
      return root + '/products/' + encodeURIComponent(product.handle) + '?variant=' + encodeURIComponent(item.variant_id);
    }

    money(cents, convert = true) {
      const currency = this.api.get_active_currency?.() || 'EUR';
      const rate = convert ? Number(this.api.get_current_currency_rate?.() || 1) : 1;
      return new Intl.NumberFormat(document.documentElement.lang || 'de', { style: 'currency', currency }).format(cents * rate / 100);
    }

    render(items) {
      this.itemsElement.replaceChildren();
      this.statusElement.hidden = true;
      this.emptyElement.hidden = items.length > 0;
      this.footerElement.hidden = items.length === 0;
      let subtotal = 0;
      let pricesComplete = true;

      items.forEach((item) => {
        const product = this.products[item.product_id];
        const variant = product?.variants?.find((value) => String(value.id) === String(item.variant_id));
        const row = node('li', 'quote-drawer__item');
        row.dataset.variantId = item.variant_id;
        const imageHolder = node('div');
        const details = node('div');
        if (product) {
          const url = this.productUrl(product, item);
          this.addImage(imageHolder, product, item);
          this.addVendor(details, product);
          const title = node('a', 'quote-drawer__title', product.title);
          title.href = url;
          details.append(title);
          if (variant?.title && variant.title !== 'Default Title') {
            details.append(node('p', 'quote-drawer__variant', variant.title));
          }
          if (variant?.sku) details.append(node('p', 'quote-drawer__variant', variant.sku));
        } else {
          details.append(node('p', 'quote-drawer__title', this.dataset.unavailable));
        }

        if (item.total_extra_option && typeof item.total_extra_option === 'object') {
          const properties = node('div', 'quote-drawer__properties');
          Object.entries(item.total_extra_option).filter(([key]) => !key.startsWith('_')).forEach(([key, option]) => {
            const value = typeof option === 'object' ? option.value : option;
            if (value !== undefined && value !== '') properties.append(node('div', '', key + ': ' + String(value)));
          });
          details.append(properties);
        }

        if (variant && Number.isFinite(Number(variant.price))) {
          const linePrice = (Number(variant.price) + Number(item.total_extra_price || 0) * 100) * Number(item.qty || 1);
          subtotal += linePrice;
          const priceGroup = node('div', 'quote-drawer__price');
          const salePrice = node('span', 'price__current', this.money(linePrice));
          salePrice.setAttribute('aria-label', this.dataset.salePrice + ': ' + this.money(linePrice));
          priceGroup.append(salePrice);
          details.append(priceGroup);
          this.addComparePrice(priceGroup, product, variant, item);
        } else {
          pricesComplete = false;
        }

        const actions = node('div', 'quote-drawer__actions');
        const quantity = node('div', 'quote-drawer__quantity');
        const minus = node('button', '', '−');
        minus.type = 'button';
        minus.dataset.quoteAction = 'minus';
        minus.setAttribute('aria-label', this.dataset.decrease);
        minus.disabled = Number(item.qty) <= 1;
        const input = node('input');
        input.type = 'number';
        input.min = '1';
        input.step = '1';
        input.value = String(item.qty || 1);
        input.inputMode = 'numeric';
        input.dataset.quoteQuantity = '';
        input.setAttribute('aria-label', this.dataset.quantity + ': ' + (product?.title || this.dataset.unavailable));
        const plus = node('button', '', '+');
        plus.type = 'button';
        plus.dataset.quoteAction = 'plus';
        plus.setAttribute('aria-label', this.dataset.increase);
        quantity.append(minus, input, plus);
        const remove = node('button', 'quote-drawer__remove btn btn--icon text-current tap-target');
        remove.append(this.querySelector('[data-quote-trash]').content.cloneNode(true));
        remove.title = this.dataset.remove;
        remove.type = 'button';
        remove.dataset.quoteAction = 'remove';
        remove.setAttribute('aria-label', this.dataset.remove + ': ' + (product?.title || this.dataset.unavailable));
        actions.append(quantity, remove);
        details.append(actions);
        row.append(imageHolder, details);
        this.itemsElement.append(row);
      });

      this.querySelector('[data-quote-drawer-subtotal-row]').hidden = !pricesComplete;
      this.querySelector('[data-quote-drawer-subtotal]').textContent = pricesComplete ? this.money(subtotal) : '';
    }

    changeItem(event) {
      const button = event.target.closest('[data-quote-action]');
      if (!button || !this.contains(button)) return;
      const row = button.closest('[data-variant-id]');
      const input = row.querySelector('[data-quote-quantity]');
      if (button.dataset.quoteAction === 'remove') {
        this.saveChange(row.dataset.variantId, null, 'remove');
      } else {
        const value = Math.max(1, Number(input.value) + (button.dataset.quoteAction === 'plus' ? 1 : -1));
        this.saveChange(row.dataset.variantId, value, button.dataset.quoteAction);
      }
    }

    changeQuantity(event) {
      if (!event.target.matches('[data-quote-quantity]')) return;
      const input = event.target;
      const quantity = Number(input.value);
      if (!Number.isSafeInteger(quantity) || quantity < 1) {
        input.value = String(this.api.get_added_item_list().find((item) => String(item.variant_id) === input.closest('[data-variant-id]').dataset.variantId)?.qty || 1);
        return;
      }
      this.saveChange(input.closest('[data-variant-id]').dataset.variantId, quantity, 'input');
    }

    saveChange(variantId, quantity, focusAction) {
      const api = this.api;
      if (!api) return;
      const items = api.get_added_item_list();
      const updated = quantity === null ? items.filter((item) => String(item.variant_id) !== variantId)
        : items.map((item) => String(item.variant_id) === variantId ? { ...item, qty: quantity } : item);
      if (!api.set_added_item_list(updated)) {
        this.statusElement.textContent = this.dataset.error;
        this.statusElement.hidden = false;
        return;
      }
      this.syncApp();
      this.render(updated);
      this.refreshFocus();
      this.querySelector('[data-quote-drawer-announcement]').textContent = quantity === null ? this.dataset.removed : this.dataset.updated;
      const row = [...this.itemsElement.children].find((element) => element.dataset.variantId === variantId);
      const focus = row?.querySelector(focusAction === 'input' ? '[data-quote-quantity]' : '[data-quote-action="' + focusAction + '"]');
      (focus && !focus.disabled ? focus : row?.querySelector('[data-quote-quantity]') || this.querySelector('.js-close-drawer')).focus({ preventScroll: true });
    }

    syncApp() {
      const api = this.api;
      if (!api) return;
      api.mark_unmark_all_btns_by_added_list?.();
      api.add_counter_to_link?.();
      api.raq_widget_qty();
    }

    async afterAdd(event) {
      const button = event.target.closest('.js-product-form-main .prod-page-raq-btn-holder .sc-raq-add-to-quote-btn');
      if (!button || button.closest('quick-add-drawer, .card')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (button.disabled || this.adding) return;
      const form = button.closest('.js-product-form-main');
      const quantityInput = form.querySelector('input[name="quantity"]');
      const quantity = Number(quantityInput?.value || 1);
      if (!Number.isSafeInteger(quantity) || quantity < 1) {
        quantityInput?.reportValidity();
        return;
      }
      const variantId = button.getAttribute('scr-vid');
      const productId = button.getAttribute('scr-pid');
      const selected = [...document.querySelectorAll('.st_multi_upsell_item .st_single-upsell-checkbox-input:checked:not(:disabled)')]
        .filter((input) => input.closest('.st_multi_upsell_item').getClientRects().length)
        .map((input) => {
          const row = input.closest('.st_multi_upsell_item');
          const link = row.querySelector('.st_multi_title[href*="/products/"]');
          return {
            variantId: input.dataset.variantId?.split('/').pop(),
            handle: link ? new URL(link.href).pathname.split('/products/')[1]?.split('/')[0] : null
          };
        });
      this.adding = true;
      button.disabled = true;
      const error = form.querySelector('[data-quote-add-error]');
      if (error) error.remove();
      let before;
      let changed = false;
      try {
        const api = await this.ready();
        const additions = await Promise.all(selected.map(async (item) => {
          if (!item.variantId || !item.handle) throw new Error('Upsell selection unavailable');
          const product = await this.nativeProduct(decodeURIComponent(item.handle));
          const variant = product?.variants?.find((entry) => String(entry.id) === item.variantId);
          if (!variant) throw new Error('Upsell variant unavailable');
          return { variant_id: item.variantId, product_id: String(product.id), qty: 1 };
        }));
        before = api.get_added_item_list().map((item) => ({ ...item }));
        const existing = before.find((item) => String(item.variant_id) === variantId);
        let updated;
        if (existing) {
          updated = before.map((item) => String(item.variant_id) === variantId ? { ...item, qty: Number(item.qty || 1) + quantity } : item);
        } else {
          if (!api.add_item_to_list(variantId, quantity, productId)) throw new Error('Quote item could not be saved');
          changed = true;
          updated = api.get_added_item_list().map((item) => ({ ...item }));
        }
        const added = new Set();
        additions.forEach((item) => {
          if (added.has(item.variant_id)) return;
          added.add(item.variant_id);
          const index = updated.findIndex((entry) => String(entry.variant_id) === item.variant_id);
          if (index >= 0) updated[index] = { ...updated[index], qty: Number(updated[index].qty || 1) + item.qty };
          else updated.push(item);
        });
        if (!api.set_added_item_list(updated)) throw new Error('Quote selection could not be saved');
        this.syncApp();
        this.open(button);
      } catch (error) {
        if (changed && before) this.api?.set_added_item_list(before);
        this.syncApp();
        const message = node('p', 'text-error-text mt-3', this.dataset.error);
        message.dataset.quoteAddError = '';
        message.setAttribute('role', 'alert');
        button.closest('.product-info__add-to-cart').after(message);
      } finally {
        this.adding = false;
        button.disabled = false;
      }
    }
  }
  customElements.define('quote-drawer', QuoteDrawer);
})();
